import React, { useEffect, useState } from "react";
import { useProductStore } from "../../app/search-results/product-store/context";
import Icon from "../icon";
import { setRegimeFlyInIsOpen } from "../../app/search-results/product-store/reducer";
import { setBookingPackage, setRooms } from "../../app/search-results/qsm-store/reducer";
import { useQSMStore } from "../../app/search-results/qsm-store/context";
import { Room } from "../../app/search-results/qsm/room-picker";
import AmountInput from "../../app/search-results/qsm/amount-input";
import { cloneDeep, first, groupBy, isEqual, last, range } from "lodash";
import { formatter, selectCorrectRoomInBookingPackage, setImageParameters } from "../../utils";
import {
  Image,
  TideItemForAccomodation,
  TideItemForHotel,
  TideItemForRoundtrip,
} from "../../types";
import { BookingPackageRoomOption } from "@qite/tide-client/build/types";
import LightBox from "react-image-lightbox";
import { sortProps } from "../../app/search-results/qsm/utils";
import AgeSelect from "../../app/search-results/qsm/age-select";
import Loader from "../loader";
import { BookingPackageOption } from "@qite/tide-client/build/types/offer/booking-v2/shared/booking-package-option";
import { useI18next } from "gatsby-plugin-react-i18next";

interface FlyInRoomProps {
  product: TideItemForHotel | TideItemForRoundtrip;
  isRoundTrip: boolean;
}

interface AccoGroup {
  key: string;
  value: BookingPackageRoomOption[];
}

const RoomFlyIn: React.FC<FlyInRoomProps> = ({ product, isRoundTrip }) => {
  const [productState, productDispatch] = useProductStore();
  const [qsmState, qsmDispatch] = useQSMStore();
  const [showRoomIndex, setShowRoomIndex] = useState<number | undefined>(0);
  const [isBeingEdited, setIsBeingEdited] = useState<boolean>(false);
  const [isFormActive, setIsFormActive] = useState<boolean>(false);
  const [requestRooms, setRequestRooms] = useState<Room[]>([]);
  const [roomToEdit, setRoomToEdit] = useState<Room>({
    adults: 2,
    children: 0,
    childAges: [],
    isVisible: false,
  });

  const [imageIndex, setImageIndex] = useState(0);
  const [images, setImages] = useState<Image[] | undefined>();
  const [lightBoxIsOpen, setLightBoxIsOpen] = useState(false);
  const [activeOption, setActiveOption] = useState<BookingPackageOption>();
  const { t } = useI18next();

  const placeholderImage =
    "https://media.tidesoftware.be/media/10/Products/Placeholders/placeholder_accommodation-card-v2-1.webp";

  const toggleFormActive = () => {
    setIsFormActive(!isFormActive);
  };

  const editRoom = (index?: number) => {
    setShowRoomIndex(index != undefined ? index : undefined);

    if (index != undefined) {
      setRoomToEdit({ ...requestRooms[index] });
    }
    toggleFormActive();
  };

  const addRoom = () => {
    if (showRoomIndex == undefined) {
      var arr = requestRooms.concat({ ...roomToEdit });
      setRequestRooms(arr);
      confirmRoomDistribution(arr);
      setShowRoomIndex(arr.length - 1);
    } else {
      const newState = requestRooms.map((room, i) => {
        if (i === showRoomIndex) {
          return roomToEdit;
        } else {
          return room;
        }
      });
      setRequestRooms(newState);
      confirmRoomDistribution(newState);
    }
    setRoomToEdit({ adults: 2, children: 0, childAges: [], isVisible: false });
    toggleFormActive();
  };

  const removeRoom = (index: number) => {
    var rooms = [...requestRooms];
    rooms.splice(index, 1);
    setRequestRooms(rooms);
    confirmRoomDistribution(rooms);
  };

  const handleSelectRoom = (event: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    event.preventDefault();
    setShowRoomIndex(index);
  };

  const handleClose = () => {
    if (productDispatch) productDispatch(setRegimeFlyInIsOpen(false));
  };

  const confirmRoomDistribution = (rooms: Room[]) => {
    if (qsmDispatch && rooms) {
      qsmDispatch(setRooms(rooms.map((object) => ({ ...object }))));

      if (qsmState?.bookingPackage) {
        const bookingPackage = selectCorrectRoomInBookingPackage(
          rooms,
          cloneDeep(qsmState?.bookingPackage)
        );
        qsmDispatch(setBookingPackage(bookingPackage));
      }
    }
  };

  const confirmOptions = () => {
    confirmRoomDistribution(requestRooms);
    handleClose();
  };

  const groupByAcco = (index: number) => {
    if (activeOption?.rooms[index]) {
      const grouped = groupBy(activeOption?.rooms[index].options, "accommodationCode");
      let keys = Object.keys(grouped);

      return keys.map((x) => ({ key: x, value: grouped[x] } as AccoGroup));
    }
  };

  const getAccommodation = (code: string) => {
    return ((product.childItems as TideItemForAccomodation[]) ?? []).find(
      (x) => x.content?.general?.productcode == code
    );
  };

  const getAccommodationName = (gr: AccoGroup) => {
    return getAccommodation(gr.key)?.content?.general?.title ?? first(gr.value)?.accommodationName;
  };

  const getAccommodationFacilities = (code: string) => {
    return getAccommodation(code)?.content?.description?.facilities?.split("\n");
  };

  const getDefaultImage = (code: string) => {
    return getAccommodation(code)?.content?.description?.picture;
  };

  const getNumberOfImages = (code: string) => {
    let numberOfExtraImages =
      getAccommodation(code)?.content?.description?.extraImages?.length ?? 0;
    if (getDefaultImage(code)) numberOfExtraImages += 1;

    return numberOfExtraImages;
  };

  const getExtraImages = (code: string) => {
    return getAccommodation(code)?.content?.description?.extraImages;
  };

  const isSelectedRoom = (index: number, code: string, regimeCode?: string) => {
    const selectedAcco = getSelectedAccoCode(index);

    if (selectedAcco == code) {
      const selectedRegime =
        requestRooms[index].regimeCode ??
        activeOption?.rooms[index]?.options.find((x) => x.isSelected)?.regimeCode;
      if (selectedRegime != regimeCode) {
        return false;
      }

      return true;
    }

    return false;
  };

  const getSelectedAccoCode = (roomIndex: number) => {
    return (
      requestRooms[roomIndex].accommodationCode ??
      activeOption?.rooms[roomIndex]?.options.find((x) => x.isSelected)?.accommodationCode
    );
  };

  const selectAccommodation = (roomIndex: number, gr: AccoGroup) => {
    let roomsCopy = [...requestRooms];

    if (roomsCopy[roomIndex].accommodationCode !== gr.key) {
      roomsCopy[roomIndex].accommodationCode = gr.key;
      roomsCopy[roomIndex].regimeCode = first(gr.value)?.regimeCode ?? undefined;
      setRequestRooms(roomsCopy);
    }
  };

  const handleOptionChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    roomIndex: number,
    code: string
  ) => {
    event.preventDefault();
    let roomsCopy = [...requestRooms];
    roomsCopy[roomIndex].accommodationCode = code;
    roomsCopy[roomIndex].regimeCode = event.target.value != "" ? event.target.value : undefined;
    setRequestRooms(roomsCopy);
  };

  const handleViewImages = (event: React.MouseEvent<HTMLAnchorElement>, code: string) => {
    if (typeof window !== "undefined" && typeof document !== "undefined" && !isRoundTrip) {
      event.preventDefault();
      let extraImages = getExtraImages(code) ? (getExtraImages(code) as Image[]) : [];
      if (getDefaultImage(code)) {
        extraImages = [...extraImages.concat(getDefaultImage(code)!)];
      }

      setImages(extraImages);
      setImageIndex(0);
      if (extraImages.length) {
        setLightBoxIsOpen(true);
      }
    }
  };

  const getRoomPriceDifference = (index: number, code: string, regimeCode?: string) => {
    if (isSelectedRoom(index, code, regimeCode)) {
      return 0;
    } else {
      const options = activeOption?.rooms[index]?.options;
      const selectedOption = options?.find(
        (x) =>
          x.accommodationCode == requestRooms[index].accommodationCode &&
          x.regimeCode == requestRooms[index].regimeCode
      );
      const selectedBookingOption = options?.find((x) => x.isSelected);

      let desiredOption = options?.find((x) => x.accommodationCode == code);
      if (regimeCode)
        desiredOption = options?.find(
          (x) => x.accommodationCode == code && x.regimeCode == regimeCode
        );

      if ((selectedBookingOption || selectedOption) && desiredOption) {
        const priceDifference =
          desiredOption?.price -
          (selectedOption?.price ? selectedOption?.price : selectedBookingOption?.price ?? 0);
        return priceDifference;
      }
    }

    return 0;
  };

  const getRoomsPriceDifference = () => {
    if (activeOption?.rooms) {
      let originalOptionsPrice = 0;
      let desiredOptionsPrice = 0;
      for (let index = 0; index < requestRooms.length; index++) {
        const room = activeOption.rooms[index];

        const originalOption = room?.options?.find((x) => x.isSelected);
        originalOptionsPrice += originalOption?.price ?? 0;

        const desiredOption = room?.options?.find(
          (x) =>
            x.accommodationCode == requestRooms[index].accommodationCode &&
            x.regimeCode == requestRooms[index].regimeCode
        );
        desiredOptionsPrice += desiredOption ? desiredOption.price : originalOption?.price ?? 0;
      }
      if (desiredOptionsPrice && originalOptionsPrice) {
        return desiredOptionsPrice - originalOptionsPrice;
      }
    }
    return 0;
  };

  const getFlightPriceDifference = () => {
    if (qsmState?.bookingPackage && qsmState.flight) {
      let originalFlightsPrice = 0;
      let desiredFlightsPrice = 0;

      originalFlightsPrice +=
        qsmState?.bookingPackage.outwardFlights.find((x) => x.isSelected)?.price ?? 0;
      originalFlightsPrice +=
        qsmState?.bookingPackage.returnFlights.find((x) => x.isSelected)?.price ?? 0;

      desiredFlightsPrice += qsmState.flight.outward.price ?? 0;
      desiredFlightsPrice += qsmState.flight.return.price ?? 0;

      if (desiredFlightsPrice && originalFlightsPrice) {
        return desiredFlightsPrice - originalFlightsPrice;
      }
    }
    return 0;
  };

  const getTotalPriceDifference = () => {
    const flightPriceDifference = getFlightPriceDifference();
    const roomsPriceDifference = getRoomsPriceDifference();

    return flightPriceDifference + roomsPriceDifference;
  };

  const getSelectedRegimeCode = (gr: AccoGroup, roomIndex: number) => {
    if (gr.key == requestRooms[roomIndex].accommodationCode) {
      return (
        gr.value.find((x) => x.regimeCode == requestRooms[roomIndex].regimeCode)?.regimeCode ??
        undefined
      );
    }
    return gr.value.find((x) => x.isSelected)?.regimeCode ?? undefined;
  };

  useEffect(() => {
    var qsmStateJSON = JSON.stringify(
      qsmState?.rooms?.map(sortProps).map((x) => {
        return { adults: x.adults, children: x.children, childAges: x.childAges } as Room;
      })
    );
    var requestRoomsJSON = JSON.stringify(
      requestRooms.map(sortProps).map((x) => {
        return { adults: x.adults, children: x.children, childAges: x.childAges } as Room;
      })
    );
    if (!isEqual(qsmStateJSON, requestRoomsJSON)) {
      setIsBeingEdited(true);
    } else {
      setIsBeingEdited(false);
    }
  }, [requestRooms, qsmState?.rooms]);

  useEffect(() => {
    if (productState?.regimeFlyInIsOpen && qsmState?.rooms) {
      const copy = qsmState.rooms.map((object) => ({ ...object }));
      setRequestRooms(copy ?? []);
    }
  }, [productState?.regimeFlyInIsOpen]);

  useEffect(() => {
    const bookingPackage = qsmState?.bookingPackage;

    if (bookingPackage) {
      const option = bookingPackage.options.find((x) => x.isSelected);
      setActiveOption(option);
    }
  }, [qsmState, qsmState?.bookingPackage]);

  return (
    <div className={`fly-in ${productState?.regimeFlyInIsOpen ? "fly-in--active" : ""}`}>
      <div className={`fly-in__form--container ${isFormActive ? "" : "fly-in__form--hidden"}`}>
        <div className="fly-in__close" onClick={() => toggleFormActive()}>
          <Icon name="ui-close" />
        </div>
        <h3 className="fly-in__form--title">
          {t("ROOM")} {showRoomIndex != undefined ? showRoomIndex + 1 : requestRooms.length + 1}
        </h3>
        <AmountInput
          label={t("HOW_MANY_ADULTS")}
          value={roomToEdit.adults}
          min={1}
          isLoading={qsmState?.isLoading}
          onChange={(value) => {
            setRoomToEdit({ ...roomToEdit, adults: value });
          }}
        />
        <AmountInput
          label={t("HOW_MANY_CHILDREN")}
          value={roomToEdit.children}
          isLoading={qsmState?.isLoading}
          onChange={(value) => {
            setRoomToEdit({
              ...roomToEdit,
              children: value,
              childAges: range(0, value).map((childIndex) => roomToEdit.childAges[childIndex] ?? 0),
            });
          }}
        />
        {roomToEdit.children > 0 && (
          <div className="configure-rooms__group-children">
            <label className="room-line__ages-label">{t("AGE_BY_DEPARTURE_DATE")}</label>
            <div className="dropdown-group">
              {range(0, roomToEdit.children).map((childIndex) => (
                <AgeSelect
                  key={childIndex}
                  isLoading={qsmState?.isLoading}
                  value={roomToEdit.childAges[childIndex]}
                  onChange={(value) => {
                    setRoomToEdit({
                      ...roomToEdit,
                      childAges: roomToEdit.childAges
                        .map((age, i) => (childIndex === i ? value : age))
                        .sort((a, b) => b - a),
                    });
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <div className="fly-in__footer-cta fly-in__footer-cta--form">
          <button className="cta" onClick={() => addRoom()}>
            {t("TO_CONFIRM")}
          </button>
        </div>
      </div>

      <div
        className={`fly-in__backdrop ${isFormActive ? "fly-in__backdrop--form" : ""}`}
        onClick={() => handleClose()}
      ></div>

      <div className="fly-in__frame">
        <div className="fly-in__header">
          <div className="fly-in__close" onClick={() => handleClose()}>
            <Icon name="ui-close" />
          </div>
          <div className="fly-in__title">{t("EDIT_ROOM")}</div>
        </div>
        <div className="fly-in__body">
          <div className="fly-in__item">
            <div className="fly-in__item-title">
              {t("ROOMS")}
              <div className="configure-rooms__actions">
                <button className="cta cta--secondary cta--add" onClick={() => editRoom()}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#067192"
                    className="bi bi-plus-lg"
                    viewBox="0 0 16 16"
                    style={{ marginRight: "1rem" }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"
                    />
                  </svg>
                  {t("ADD_ROOM")}
                </button>
              </div>
            </div>

            <div className="fly-in__item-content">
              <div className="configure-rooms__frame">
                <div className="configure-rooms__header"></div>
                <div className="configure-rooms__body">
                  <div className="configure-rooms__groups configure-rooms__groups--product">
                    <div className="configure-rooms__groups--table">
                      <table className="configure-rooms__groups--border">
                        <tbody>
                          {requestRooms.map((requestRoom, roomIndex) => (
                            <tr
                              key={roomIndex}
                              className={`configure-rooms__center ${
                                roomIndex == showRoomIndex ? "configure-rooms__active" : ""
                              }`}
                            >
                              <td>
                                <a
                                  href="#"
                                  className="configure-rooms__check"
                                  onClick={(e) => handleSelectRoom(e, roomIndex)}
                                >
                                  {roomIndex == showRoomIndex ? (
                                    <span className="configure-rooms__check--active">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="10"
                                        height="10"
                                        fill="currentColor"
                                        className="bi bi-circle-fill"
                                        viewBox="0 0 16 16"
                                      >
                                        <circle cx="8" cy="8" r="8" />
                                      </svg>
                                    </span>
                                  ) : (
                                    <span></span>
                                  )}
                                </a>
                              </td>
                              <td>
                                <h3 className="configure-rooms__group-title">
                                  {t("ROOM")} {roomIndex + 1}
                                </h3>
                              </td>
                              <td className="configure-rooms__persons">
                                <AmountInput
                                  label={t("ADULTS")}
                                  value={requestRoom.adults}
                                  isLoading={qsmState?.isLoading}
                                  onChange={() => editRoom(roomIndex)}
                                />
                                <AmountInput
                                  label={t("CHILDREN")}
                                  value={requestRoom.children}
                                  isLoading={qsmState?.isLoading}
                                  onChange={() => editRoom(roomIndex)}
                                />
                              </td>
                              <td>
                                <span className="configure-rooms__childage">
                                  {requestRoom.children > 0 && (
                                    <span>
                                      {requestRoom.childAges.map((value) => (
                                        <>
                                          {value}
                                          {t("YEAR_SHORT")}{" "}
                                          {value == last(requestRoom.childAges) ? "" : " - "}
                                        </>
                                      ))}
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="decrement-increment--rooms">
                                {requestRoom != first(requestRooms) && (
                                  <div
                                    className="configure-rooms--remove"
                                    onClick={() => removeRoom(roomIndex)}
                                  >
                                    <Icon name="ui-trashcan" />
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {showRoomIndex != undefined && requestRooms[showRoomIndex] && (
                      <div className="configure-rooms__group">
                        <h3 className="configure-rooms__group-heading">
                          {t("ROOM_OPTIONS")}
                          {!isBeingEdited && !qsmState?.isLoading && (
                            <span className="configure-rooms__group-option">
                              {t("ROOM")} {showRoomIndex + 1}
                            </span>
                          )}
                        </h3>

                        {!isBeingEdited && !qsmState?.isLoading && (
                          <div className="configure-rooms__group-body">
                            {groupByAcco(showRoomIndex)?.map(
                              (gr: AccoGroup, accoGroupindex: number) => (
                                <div key={accoGroupindex} className="configure-rooms__item">
                                  <div className="configure-rooms__item-media">
                                    <div className="image">
                                      <a
                                        href=""
                                        title={
                                          getNumberOfImages(gr.key) > 0
                                            ? t("VIEW_IMAGES")
                                            : undefined
                                        }
                                        className="image__link"
                                        onClick={(e) => handleViewImages(e, gr.key)}
                                        data-count={
                                          getNumberOfImages(gr.key) > 0
                                            ? getNumberOfImages(gr.key)
                                            : undefined
                                        }
                                      >
                                        <img
                                          src={setImageParameters(
                                            getDefaultImage(gr.key)?.url ?? placeholderImage,
                                            {
                                              width: 160,
                                              height: 160,
                                              mode: "crop",
                                            }
                                          )}
                                          alt={getDefaultImage(gr.key)?.altText ?? "Travelworld"}
                                          className="image__content"
                                        />
                                      </a>
                                    </div>
                                  </div>
                                  <div className="configure-rooms__item-body">
                                    <div className="configure-rooms__item-header">
                                      <div className="configure-rooms__item-title">
                                        {isRoundTrip
                                          ? first(gr.value)?.accommodationName
                                          : getAccommodationName(gr)}
                                      </div>
                                      <div
                                        className={`configure-rooms__item-price ${
                                          Math.sign(
                                            getRoomPriceDifference(showRoomIndex, gr.key)
                                          ) == -1
                                            ? "configure-rooms__item-price--less"
                                            : "configure-rooms__item-price--more"
                                        }`}
                                      >
                                        {Math.sign(getRoomPriceDifference(showRoomIndex, gr.key)) !=
                                          0 &&
                                          `${
                                            Math.sign(
                                              getRoomPriceDifference(showRoomIndex, gr.key)
                                            ) == -1
                                              ? "-"
                                              : "+"
                                          } ${formatter.format(
                                            Math.abs(getRoomPriceDifference(showRoomIndex, gr.key))
                                          )}`}
                                      </div>
                                      <div className="configure-rooms__item-cta">
                                        <button
                                          className={`cta ${
                                            getSelectedAccoCode(showRoomIndex) == gr.key
                                              ? "cta--selected"
                                              : "cta--secondary"
                                          }`}
                                          onClick={() => selectAccommodation(showRoomIndex, gr)}
                                        >
                                          {getSelectedAccoCode(showRoomIndex) == gr.key
                                            ? t("SELECTED")
                                            : t("SELECT")}
                                        </button>
                                      </div>
                                    </div>
                                    <div className="configure-rooms__item-content">
                                      <ul className="list list--usps">
                                        {getAccommodationFacilities(gr.key)?.map(
                                          (facility: string, facilityIndex: number) => (
                                            <li className="list__item" key={facilityIndex}>
                                              {facility}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                      <div className="dropdown">
                                        <select
                                          onChange={(e) =>
                                            handleOptionChange(e, showRoomIndex, gr.key)
                                          }
                                          value={getSelectedRegimeCode(gr, showRoomIndex)}
                                        >
                                          {gr.value.map((option, optionIndex) => (
                                            <option
                                              key={optionIndex}
                                              value={option.regimeCode ?? ""}
                                            >
                                              {option.regimeName ??
                                                option.regimeCode ??
                                                t("ROOM_ONLY")}{" "}
                                              {getRoomPriceDifference(
                                                showRoomIndex,
                                                gr.key,
                                                option.regimeCode
                                              ) != 0 &&
                                                `${
                                                  Math.sign(
                                                    getRoomPriceDifference(
                                                      showRoomIndex,
                                                      gr.key,
                                                      option.regimeCode
                                                    )
                                                  ) == -1
                                                    ? "-"
                                                    : "+"
                                                } ${formatter.format(
                                                  Math.abs(
                                                    getRoomPriceDifference(
                                                      showRoomIndex,
                                                      gr.key,
                                                      option.regimeCode
                                                    )
                                                  )
                                                )}`}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {qsmState?.isLoading && <Loader loaderText={t("LOADING_PACKAGE")} />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="fly-in__footer--bottom">
          {!qsmState?.isLoading && (
            <>
              <div className="fly-in__footer fly-in__footer--btn fly-in__footer--desktop">
                {showRoomIndex != undefined && (
                  <>
                    {last(requestRooms) == requestRooms[showRoomIndex] ? (
                      <button className="cta cta--fullwidth" onClick={() => confirmOptions()}>
                        {t("CONFIRM_ROOM_X_AND_CLOSE", { roomNumber: showRoomIndex + 1 })}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#FFFFFF"
                          className="bi bi-check-lg"
                          viewBox="0 0 16 16"
                          style={{ marginLeft: "1rem" }}
                        >
                          <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="cta cta--fullwidth"
                        onClick={() => setShowRoomIndex(showRoomIndex + 1)}
                      >
                        {t("CONFIRM_ROOM_X_AND_NEXT", { roomNumber: showRoomIndex + 1 })}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#FFFFFF"
                          className="bi bi-chevron-left"
                          viewBox="0 0 16 16"
                          style={{ marginLeft: "1rem", transform: "rotate(180deg)" }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                          />
                        </svg>
                      </button>
                    )}

                    <button
                      className={`cta cta--secondary cta--fullwidth ${
                        first(requestRooms) == requestRooms[showRoomIndex] ? "cta--disabled" : ""
                      }`}
                      disabled={first(requestRooms) == requestRooms[showRoomIndex]}
                      onClick={() => setShowRoomIndex(showRoomIndex - 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="#067192"
                        className="bi bi-chevron-left"
                        viewBox="0 0 16 16"
                        style={{ marginRight: "1rem" }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                        />
                      </svg>
                      {t("PREVIOUS_ROOM")}
                    </button>
                  </>
                )}
              </div>

              <div className="fly-in__footer fly-in__footer--btn fly-in__footer--mobile">
                {showRoomIndex != undefined && (
                  <>
                    {last(requestRooms) == requestRooms[showRoomIndex] ? (
                      <button className="cta cta--fullwidth" onClick={() => confirmOptions()}>
                        {t("TO_CONFIRM")}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#FFFFFF"
                          className="bi bi-check-lg"
                          viewBox="0 0 16 16"
                          style={{ marginLeft: "1rem" }}
                        >
                          <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="cta cta--fullwidth cta--nowrap"
                        onClick={() => setShowRoomIndex(showRoomIndex + 1)}
                      >
                        {t("NEXT_ROOM")}{" "}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#FFFFFF"
                          className="bi bi-chevron-left"
                          viewBox="0 0 16 16"
                          style={{ marginLeft: "1rem", transform: "rotate(180deg)" }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                          />
                        </svg>
                      </button>
                    )}

                    <button
                      className={`cta cta--secondary cta--fullwidth ${
                        first(requestRooms) == requestRooms[showRoomIndex] ? "cta--disabled" : ""
                      }`}
                      disabled={first(requestRooms) == requestRooms[showRoomIndex]}
                      onClick={() => setShowRoomIndex(showRoomIndex - 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="#067192"
                        className="bi bi-chevron-left"
                        viewBox="0 0 16 16"
                        style={{ marginRight: "1rem" }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                        />
                      </svg>{" "}
                      {t("GO_BACK")}
                    </button>
                  </>
                )}
              </div>

              <div className="fly-in__footer">
                <div className="fly-in__footer-row fly-in__footer-row--moreprice">
                  <div className="fly-in__footer-label">{t("ADDITIONAL_COST")}</div>
                  <div className="fly-in__footer-price">{`${
                    Math.sign(getRoomsPriceDifference()) == -1 ? "-" : "+"
                  } ${formatter.format(Math.abs(getRoomsPriceDifference()))}`}</div>
                </div>
                <div className="fly-in__footer-row fly-in__footer-row--totalprice">
                  <div className="fly-in__footer-label">{t("TOTAL_PRICE")}</div>
                  <div className="fly-in__footer-price">
                    {productState?.price?.defaultPrice && (
                      <>
                        {formatter.format(
                          productState?.price?.defaultPrice + getTotalPriceDifference()
                        )}
                      </>
                    )}
                    {!productState?.price?.defaultPrice &&
                      productState?.estimatedPrice?.promoPrice && (
                        <>
                          {t("STARTING_FROM_SHORT")}{" "}
                          {formatter.format(productState?.estimatedPrice?.promoPrice)}{" "}
                          {t("PER_PERSON_SHORT")}
                        </>
                      )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {lightBoxIsOpen && images?.length && (
        <LightBox
          imageTitle={images[imageIndex]!.altText}
          imageCaption={`${imageIndex + 1} / ${images.length}`}
          mainSrc={images[imageIndex]?.url ?? ""}
          nextSrc={images[(imageIndex + 1) % images.length]?.url ?? ""}
          prevSrc={images[(imageIndex + images.length - 1) % images.length]?.url ?? ""}
          onCloseRequest={() => setLightBoxIsOpen(false)}
          onMovePrevRequest={() => setImageIndex((imageIndex + images.length - 1) % images.length)}
          onMoveNextRequest={() => setImageIndex((imageIndex + 1) % images.length)}
        />
      )}
    </div>
  );
};

export default RoomFlyIn;
